import { useCallback, useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import shortid from 'shortid';
import type { VoidFunctionComponent } from 'react';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import Source from './Source';
import Button, { LoaderButton, NavigationButton, ButtonGroup } from '../../../generic/Button';
import { IconText, icons } from '../../../generic/Icon';
import { DOCS_URL } from '../../../generic/Link';
import { AccessGate } from '../../../generic/Gates';
import { dropAt } from '../../../../utils';
import { post } from '../../../../redux/api';
import Loader from '../../../generic/Loader';
import { useAsyncValue } from '../../../../hooks';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import { window } from '../../../../globals';
import type { NugetSource } from '../../../../../typings/auth';
import type { FailedFetchResponse } from '../../../../helpers/errors';
import { ErrorAlert } from '../../../generic';

const NUGET_DOCS_LINK = `${DOCS_URL}/dotnet`;

const DotnetSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'nuget',
  );

  const { value: hasValidated, isLoading: isValidating, ...validationAsyncValue } = useAsyncValue<boolean>(false);
  const sources = useMemo(() => settings?.sources || [], [settings]);

  const onAddSource = () =>
    onUpdateSettings({
      sources: [
        ...sources,
        { _id: shortid.generate() },
      ],
    });

  const onRemoveSource = (index: number) =>
    onUpdateSettings({
      sources: dropAt(sources, index),
    });

  const onUpdateSource = <K extends keyof NugetSource>(
    index: number,
    member: K,
    value: NugetSource[K],
  ) =>
      onUpdateSettings({
        sources: sources.map((source, i): NugetSource => {
          if (i !== index) {
            return source;
          }

          return {
            ...source,
            [member]: value,
            hasPassword: member === 'password' ? false : source.hasPassword,
          };
        }),
      });

  const validateSources = useCallback(async () => {
    //  Note that we disable the button anyway, so this shouldn't ever happen.
    if (!sources.length) {
      return;
    }
    validationAsyncValue.setPending();
    try {
      await post('/api/organizations/validateSources', { sources });
      //  We use the value to tell the UI we should display the success alert.
      validationAsyncValue.setResolve(true);
    } catch (error) {
      validationAsyncValue.setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
    }
  }, [sources, validationAsyncValue]);

  return (
    <AccessGate level="premium">
      <Header heading=".NET Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled || isLoading || isValidating}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Nuget sources">
              <Section.Paragraph>
                Configure your Nuget sources.
              </Section.Paragraph>
              <NavigationButton
                to={NUGET_DOCS_LINK}
                size="medium"
                type="secondary"
                openInNewTab
              >
                View documentation
              </NavigationButton>
            </Section.Left>
            <Section.Right>
              {sources.map((source, index) =>
                <Source
                  // eslint-disable-next-line no-underscore-dangle
                  key={source._id}
                  source={source}
                  onRemoveSource={() => onRemoveSource(index)}
                  onUpdateSource={
                    <K extends keyof NugetSource>(
                      member: K,
                      value: NugetSource[K],
                    ) => {
                      onUpdateSource(index, member, value);
                    }
                  }
                />)}
              <ButtonGroup>
                <Button
                  type="secondary"
                  size="medium"
                  onClick={onAddSource}
                  isDisabled={isSaving || isValidating}
                >
                  <IconText icon={icons.plus} text="Add source" />
                </Button>
                <LoaderButton
                  type="outline"
                  size="medium"
                  onClick={validateSources}
                  isDisabled={!sources.length}
                  isLoading={isValidating}
                >
                  Validate sources
                </LoaderButton>
              </ButtonGroup>
              {!!validationAsyncValue.errors?.length && (
                <div className="margin-top-lg">
                  <ErrorAlert status={{ errors: validationAsyncValue.errors }} />
                </div>
              )}
              {hasValidated && (
                <div className="margin-top-lg">
                  <Alert
                    bsStyle="success"
                    onDismiss={() => validationAsyncValue.setResolve(false)}
                  >
                    <span>All source URLs are valid</span>
                  </Alert>
                </div>
              )}
            </Section.Right>
          </Section>
          )}
    </AccessGate>
  );
};

export default DotnetSettings;
